import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, CreditCard, Loader2, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * サブスクリプション状態に応じて
 *   - 警告バナー (残り14日以内)
 *   - 完全ロックダウン (paused/canceled/unpaid)
 * を表示する。manager のみ全機能、staff はバナーのみ。
 *
 * 402 レスポンス検知時に `billing:refresh` イベントが api.js から発火され、再取得する。
 */
export default function SubscriptionGate({ user, children }) {
  const [state, setState] = useState(null);
  const [opening, setOpening] = useState(false);

  const refresh = useCallback(() => {
    if (!user || user.role === "admin") return;
    api.get("/billing/status")
      .then((r) => setState(r.data))
      .catch(() => setState(null));
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("billing:refresh", handler);
    return () => window.removeEventListener("billing:refresh", handler);
  }, [refresh]);

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/billing/portal");
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOpening(false);
    }
  };

  // admin or no state: pass through
  if (!user || user.role === "admin" || !state) return children;

  const blocked = !!state.blocked && user.role === "manager";
  const warn = !!state.warn_banner;

  return (
    <>
      {warn && !blocked && <TrialWarningBanner state={state} role={user.role} onOpenPortal={openPortal} opening={opening} />}
      {blocked && <LockdownOverlay state={state} onOpenPortal={openPortal} opening={opening} />}
      {children}
    </>
  );
}

function TrialWarningBanner({ state, role, onOpenPortal, opening }) {
  const days = state.days_remaining ?? 0;
  const tone = days <= 3 ? "bg-rose-600" : days <= 7 ? "bg-orange-500" : "bg-amber-500";
  return (
    <div
      data-testid="trial-warn-banner"
      className={`sticky top-0 z-30 ${tone} text-white text-xs font-bold px-4 py-2 flex items-center justify-center gap-3 flex-wrap`}
    >
      <AlertTriangle className="w-4 h-4" />
      <span>
        無料トライアル終了まで <b className="text-sm">あと {days} 日</b>
        {role === "manager" ? "。クレジットカードを登録すると自動で継続できます。" : "。店長さんへカード登録をご案内ください。"}
      </span>
      {role === "manager" && (
        <button
          onClick={onOpenPortal}
          disabled={opening}
          data-testid="trial-warn-cta"
          className="ml-2 px-3 py-0.5 bg-white text-slate-900 rounded text-[11px] font-bold hover:bg-slate-100 inline-flex items-center gap-1"
        >
          {opening ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
          カード登録
        </button>
      )}
    </div>
  );
}

function LockdownOverlay({ state, onOpenPortal, opening }) {
  const status = state.status;
  const title =
    status === "paused" ? "無料トライアルが終了しました"
      : status === "canceled" ? "サブスクリプションが解約されています"
        : status === "unpaid" ? "お支払いが完了していません"
          : "サブスクリプションが停止しています";
  const body =
    status === "paused"
      ? "60日間の無料期間中にクレジットカードが登録されなかったため、書き込み機能を一時停止しています。カード登録後すぐに再開できます。"
      : status === "canceled"
        ? "サブスクリプションが解約されました。再開するにはカード登録が必要です。"
        : "お支払いに失敗したためサービスを一時停止しています。カード情報をご確認ください。";

  return (
    <div
      data-testid="subscription-lockdown"
      className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl border border-rose-200 overflow-hidden">
        <div className="bg-gradient-to-br from-rose-500 to-rose-600 px-6 py-5 text-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-bold opacity-90">アクセス制限中</p>
            <h2 className="text-lg font-bold leading-tight">{title}</h2>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700 leading-relaxed">{body}</p>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 leading-relaxed">
            <p className="font-bold mb-1">📦 過去のデータは安全に保管されています</p>
            <p>確定したシフト・スタッフ・希望提出はそのまま保持され、再開後すぐに利用を再開できます。</p>
          </div>

          <Button
            onClick={onOpenPortal}
            disabled={opening}
            data-testid="lockdown-open-portal"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-5 text-sm rounded-xl inline-flex items-center justify-center gap-2"
          >
            {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            クレジットカードを登録してサービスを再開する
          </Button>

          <p className="text-[11px] text-slate-400 text-center leading-relaxed">
            登録は Stripe の安全な決済画面で行われます。<br />
            ¥3,980 / 月（モニタークーポン適用済みの場合は初月から¥1,990）
          </p>
        </div>
      </div>
    </div>
  );
}
